import { createClient, type AuthChangeEvent, type Session, type SupabaseClient } from "@supabase/supabase-js";
import type {
  Area,
  Attachment,
  ChecklistItem,
  DataExport,
  EntityId,
  Heading,
  Preferences,
  Profile,
  Project,
  ProjectSummary,
  Reminder,
  RepeatRule,
  SomedayReview,
  Tag,
  Task,
  TaskComment,
  TaskSummary,
  WeeklyFocus,
} from "../../domain/entities";
import type { MutationContext, ProjectArchiveStrategy, ReorderItem } from "../../domain/commands";
import type { PageRequest, PageResult, ProjectFilter, ProjectSort, SearchHit, SearchQuery, TaskFilter, TaskSort } from "../../domain/queries";
import type { AuthEvent, AuthSession, Backend, CreatedPersonalAccessToken, ImportResult, PersonalAccessToken, RealtimeEntity, RealtimeEvent } from "../contracts";
import { BackendError } from "../errors";
import { normalizeLegacyProjectSummary, normalizeLegacyTaskSummary } from "../../domain/scheduleMigration";
import { projectAttention } from "../../domain/projectAttention";
import { parseTaskNumber, taskNumberLabel } from "../../app/taskNumber";
import { attachmentBase64, inspectAttachmentImport } from "./attachmentPortability";
import { uploadResumable } from "./uploadResumable";
import { ATTACHMENT_BUCKET } from "../../domain/attachments";

interface SupabaseConfig {
  url: string;
  publishableKey: string;
}

type QueryError = { message: string; code?: string | undefined; details?: string | undefined; hint?: string | undefined };
type QueryResult<T> = { data: T | null; error: QueryError | null; count?: number | null };

const TASK_SELECT = "*,area:areas(*),project:projects(*),heading:headings(*),task_tags(tag:tags(*)),checklist:checklist_items(*)";
const PROJECT_SELECT = "*,area:areas(*),project_tags(tag:tags(*)),weekly_focus(*)";

export function createSupabaseBackend(config: SupabaseConfig): Backend {
  const client = createClient(config.url, config.publishableKey, {
    auth: { autoRefreshToken: true, persistSession: true, detectSessionInUrl: true },
  });

  const rpc = async <T>(name: string, payload: Record<string, unknown> = {}, signal?: AbortSignal): Promise<T> => {
    if (signal?.aborted) throw new BackendError("unknown", "The operation was cancelled.");
    const result = await client.rpc(name, snakeize(payload) as never) as QueryResult<unknown>;
    return camelize(unwrap(result)) as T;
  };
  const rpcVoid = async (name: string, payload: Record<string, unknown> = {}, signal?: AbortSignal): Promise<void> => {
    if (signal?.aborted) throw new BackendError("unknown", "The operation was cancelled.");
    const result = await client.rpc(name, snakeize(payload) as never) as QueryResult<unknown>;
    unwrapVoid(result);
  };

  const backend: Backend = {
    kind: "supabase",
    capabilities: { realtime: true, offlineQueue: false, fileStorage: true, serverSideRecurrence: true, atomicBulkMutations: true },
    auth: {
      async getSession() {
        const { data, error } = await client.auth.getSession();
        if (error) throw mapError(error);
        return data.session ? mapSession(data.session) : null;
      },
      async signIn(email, password) {
        const { data, error } = await client.auth.signInWithPassword({ email, password });
        if (error) throw mapError(error);
        if (!data.session) throw new BackendError("unauthenticated", "Sign in did not create a session.");
        return mapSession(data.session);
      },
      async signUp(input) {
        const options = input.redirectUrl
          ? { data: { display_name: input.displayName }, emailRedirectTo: input.redirectUrl }
          : { data: { display_name: input.displayName } };
        const { data, error } = await client.auth.signUp({ email: input.email, password: input.password, options });
        if (error) throw mapError(error);
        return data.session ? mapSession(data.session) : null;
      },
      async signOut() { const { error } = await client.auth.signOut(); if (error) throw mapError(error); },
      async requestPasswordReset(email, redirectTo) { const { error } = await client.auth.resetPasswordForEmail(email, { redirectTo }); if (error) throw mapError(error); },
      async updatePassword(password) { const { error } = await client.auth.updateUser({ password }); if (error) throw mapError(error); },
      subscribe(listener) {
        const { data } = client.auth.onAuthStateChange((event, session) => listener(mapAuthEvent(event, session)));
        return () => data.subscription.unsubscribe();
      },
    },
    profile: {
      async get() { return selectSingle<Profile>(client, "profiles", "*"); },
      async update(input, context) { return updateSingle<Profile>(client, "profiles", await currentUserId(client), input, context); },
    },
    preferences: {
      async get() { return selectSingle<Preferences>(client, "preferences", "*"); },
      async update(input, context) { return updateSingle<Preferences>(client, "preferences", await currentUserId(client), input, context); },
    },
    areas: {
      async list(input) {
        let query = client.from("areas").select("*").order("position");
        if (!input?.includeArchived) query = query.is("archived_at", null);
        return camelize(unwrap(await query)) as Area[];
      },
      async get(id) { return selectById<Area>(client, "areas", id); },
      async create(input, context) { return insertOne<Area>(client, "areas", withMutation(input, context)); },
      async update(id, input, context) { return updateSingle<Area>(client, "areas", id, input, context); },
      async archive(id, strategy, context) { await rpcVoid("archive_area", { areaId: id, strategy, ...mutationPayload(context) }); },
      async restore(id, context) { return updateSingle<Area>(client, "areas", id, { archivedAt: null }, context); },
      async remove(id, context) { await rpcVoid("delete_area", { areaId: id, ...mutationPayload(context) }); },
      async reorder(items, context) { return rpc<Area[]>("reorder_areas", { items, ...mutationPayload(context) }); },
    },
    projects: {
      async list(filter, sort, request) { return listProjects(client, filter, sort, request); },
      async get(id) { return getProject(client, id); },
      async create(input, context) { return rpc<Project>("create_project", { input, ...mutationPayload(context) }); },
      async update(id, input, context) { return updateSingle<Project>(client, "projects", id, { ...input, lastActivityAt: new Date().toISOString(), stallIgnoredUntil: null }, context); },
      async duplicate(id, context) { return rpc<Project>("duplicate_project", { projectId: id, ...mutationPayload(context) }); },
      async pauseInSomeday(id, context) { return rpc<Project>("pause_project_in_someday", { projectId: id, ...mutationPayload(context) }); },
      async resumeProject(id, context) { return rpc<Project>("resume_project", { projectId: id, ...mutationPayload(context) }); },
      async archiveProject(id, strategy, context) { return rpc<Project>("archive_project", { projectId: id, openTaskStrategy: strategy, ...mutationPayload(context) }); },
      async unarchiveProject(id, context) { return rpc<Project>("unarchive_project", { projectId: id, ...mutationPayload(context) }); },
      async remove(id, context) { await rpcVoid("delete_project", { projectId: id, ...mutationPayload(context) }); },
      async reorder(items, context) { return rpc<Project[]>("reorder_projects", { items, ...mutationPayload(context) }); },
      async setTags(id, tagIds, context) { return rpc<Tag[]>("set_project_tags", { projectId: id, tagIds, ...mutationPayload(context) }); },
      async listWeeklyFocus(weekStartsOn) {
        const result = await client.from("weekly_focus").select("*").eq("week_starts_on", weekStartsOn).order("position");
        return camelize(unwrap(result)) as WeeklyFocus[];
      },
      async refreshWeeklyFocus(weekStartsOn, context) { return rpc<WeeklyFocus[]>("refresh_weekly_focus", { weekStartsOn, ...mutationPayload(context) }); },
      async pinWeeklyFocus(projectId, weekStartsOn, pinned, context) { return rpc<WeeklyFocus>("pin_weekly_focus", { projectId, weekStartsOn, pinned, ...mutationPayload(context) }); },
      async excludeFromWeeklyFocus(projectId, weekStartsOn, until, context) { await rpcVoid("exclude_weekly_focus", { projectId, weekStartsOn, until, ...mutationPayload(context) }); },
      async reorderWeeklyFocus(items, context) { return rpc<WeeklyFocus[]>("reorder_weekly_focus", { items, ...mutationPayload(context) }); },
      async ignoreStall(projectId, until, context) { return updateSingle<Project>(client, "projects", projectId, { stallIgnoredUntil: until }, context); },
      async clearStallIgnore(projectId, context) { return updateSingle<Project>(client, "projects", projectId, { stallIgnoredUntil: null }, context); },
      async getSomedayReview(month) {
        const result = await client.from("someday_reviews").select("*").eq("month", month).maybeSingle();
        const value = unwrap(result);
        if (value) return camelize(value) as SomedayReview;
        return { id: "", ownerId: await currentUserId(client), createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), version: 0, month, completedAt: null };
      },
      async completeSomedayReview(month, context) { return rpc<SomedayReview>("complete_someday_review", { month, ...mutationPayload(context) }); },
    },
    headings: {
      async list(projectId) { const result = await client.from("headings").select("*").eq("project_id", projectId).order("position"); return camelize(unwrap(result)) as Heading[]; },
      async create(input, context) { return insertOne<Heading>(client, "headings", withMutation(input, context)); },
      async update(id, title, context) { return updateSingle<Heading>(client, "headings", id, { title }, context); },
      async remove(id, moveTasksToHeadingId, context) { await rpcVoid("delete_heading", { headingId: id, moveTasksToHeadingId, ...mutationPayload(context) }); },
      async reorder(items, context) { return rpc<Heading[]>("reorder_headings", { items, ...mutationPayload(context) }); },
    },
    tasks: {
      async list(filter, sort, request) { return listTasks(client, filter, sort, request); },
      async get(id) { return getTask(client, id); },
      async create(input, context) { return normalizeTaskSummary(await rpc<Record<string, unknown>>("create_task", { input, ...mutationPayload(context) })); },
      async update(id, input, context) { await updateSingle<Task>(client, "tasks", id, input, context); return getTask(client, id); },
      async duplicate(id, context) {
        const { data, error } = await client.functions.invoke("attachment-service", { body: { action: "copy-task", taskId: id, ...mutationPayload(context) } });
        if (error) throw new BackendError("unknown", "The Task and its attachments could not be copied. Retry.", { retryable: true });
        return normalizeTaskSummary(camelize(data) as Record<string, unknown>);
      },
      async complete(id, context) { return normalizeTaskSummary(await rpc<Record<string, unknown>>("transition_task", { taskId: id, transition: "complete", ...mutationPayload(context) })); },
      async cancel(id, context) { return normalizeTaskSummary(await rpc<Record<string, unknown>>("transition_task", { taskId: id, transition: "cancel", ...mutationPayload(context) })); },
      async restore(id, context) { return normalizeTaskSummary(await rpc<Record<string, unknown>>("transition_task", { taskId: id, transition: "restore", ...mutationPayload(context) })); },
      async move(id, input, context) { return normalizeTaskSummary(await rpc<Record<string, unknown>>("move_task", { taskId: id, input, ...mutationPayload(context) })); },
      async remove(id, context) { await rpcVoid("soft_delete_task", { taskId: id, ...mutationPayload(context) }); },
      async restoreDeleted(id, context) { return normalizeTaskSummary(await rpc<Record<string, unknown>>("restore_deleted_task", { taskId: id, ...mutationPayload(context) })); },
      async reorder(items, context) { const result = await rpc<Record<string, unknown>[]>("reorder_tasks", { items, ...mutationPayload(context) }); return result.map(normalizeTaskSummary); },
      async bulk(input, context) { return rpc("bulk_tasks", { input, ...mutationPayload(context) }); },
      async setTags(id, tagIds, context) { return rpc<Tag[]>("set_task_tags", { taskId: id, tagIds, ...mutationPayload(context) }); },
    },
    comments: {
      async list(taskId) {
        const [commentResult, attachmentResult] = await Promise.all([
          client.from("task_comments").select("*").eq("task_id", taskId).order("accepted_at").order("created_at"),
          client.from("attachments").select("*").eq("task_id", taskId).in("state", ["ready", "delete_pending", "deleted"]).order("position"),
        ]);
        if (commentResult.error) throw mapError(commentResult.error);
        if (attachmentResult.error) throw mapError(attachmentResult.error);
        const comments = camelize(commentResult.data ?? []) as TaskComment[];
        const attachments = camelize(attachmentResult.data ?? []) as Attachment[];
        return comments.map((comment) => ({ ...comment, attachments: attachments.filter((attachment) => attachment.commentId === comment.id && (attachment.state !== "delete_pending" || Boolean(attachment.deleteAfter && Date.parse(attachment.deleteAfter) <= Date.now()))).map((attachment) => attachment.state === "delete_pending" ? { ...attachment, state: "deleted" as const } : attachment) }));
      },
      async append(taskId, input, context) { return rpc<TaskComment>("append_task_comment", { taskId, role: input.role ?? null, body: input.body, attachmentIds: input.attachmentIds ?? [], ...mutationPayload(context) }); },
    },
    attachments: {
      async list(taskId) {
        const result = await client.from("attachments").select("*").eq("task_id", taskId).eq("state", "ready").order("ready_at", { ascending: false });
        if (result.error) throw mapError(result.error);
        return camelize(result.data ?? []) as Attachment[];
      },
      async reserve(taskId, input, context) { return rpc<Attachment>("reserve_attachment", { taskId, name: input.name, mediaType: input.mediaType, byteSize: input.byteSize, source: input.source ?? "task", ...mutationPayload(context) }); },
      async upload(id, file, options) {
        const row = await client.from("attachments").select("storage_path,media_type").eq("id", id).single();
        if (row.error) throw mapError(row.error);
        const storagePath = (row.data as { storage_path: string }).storage_path;
        const { data: sessionData, error: sessionError } = await client.auth.getSession();
        if (sessionError) throw mapError(sessionError);
        const token = sessionData.session?.access_token;
        if (!token) throw new BackendError("unauthenticated", "Sign in first.");
        await uploadResumable({ url: config.url, token, ownerId: sessionData.session!.user.id, attachmentId: id, storagePath,
          mediaType: row.data.media_type, file, ...options });
        const saved = await client.from("attachments").select("*").eq("id", id).single();
        if (saved.error) throw mapError(saved.error);
        return camelize(saved.data) as Attachment;
      },
      async finalize(id, context) {
        const { data, error } = await client.functions.invoke("attachment-service", { body: { action: "finalize", attachmentId: id, ...mutationPayload(context) } });
        if (error) throw new BackendError("validation", data?.error ?? "The file could not be verified. Retry the upload.", { retryable: true });
        return camelize(data) as Attachment;
      },
      async cancel(id, context) { await rpcVoid("cancel_attachment", { attachmentId: id, ...mutationPayload(context) }); },
      async download(id) {
        const row = await client.from("attachments").select("storage_path").eq("id", id).single();
        if (row.error) throw mapError(row.error);
        const { data, error } = await client.storage.from(ATTACHMENT_BUCKET).download((row.data as { storage_path: string }).storage_path);
        if (error) throw mapError(error);
        if (!data) throw new BackendError("not_found", "Attachment is not available.");
        return data;
      },
      async remove(id, context) { return rpc<Attachment>("remove_attachment", { attachmentId: id, ...mutationPayload(context) }); },
      async undoRemove(id, context) { return rpc<Attachment>("undo_remove_attachment", { attachmentId: id, ...mutationPayload(context) }); },
    },
    checklist: {
      async create(taskId, title, context) { return insertOne<ChecklistItem>(client, "checklist_items", withMutation({ taskId, title }, context)); },
      async update(id, input, context) { return updateSingle<ChecklistItem>(client, "checklist_items", id, input, context); },
      async remove(id, context) { await deleteById(client, "checklist_items", id, context); },
      async reorder(taskId, items, context) { return rpc<ChecklistItem[]>("reorder_checklist", { taskId, items, ...mutationPayload(context) }); },
      async promote(id, context) { return normalizeTaskSummary(await rpc<Record<string, unknown>>("promote_checklist_item", { checklistItemId: id, ...mutationPayload(context) })); },
    },
    tags: {
      async list() { const result = await client.from("tags").select("*").order("name"); return camelize(unwrap(result)) as Tag[]; },
      async create(input, context) { return insertOne<Tag>(client, "tags", withMutation(input, context)); },
      async update(id, input, context) { return updateSingle<Tag>(client, "tags", id, input, context); },
      async merge(sourceId, targetId, context) { return rpc<Tag>("merge_tags", { sourceId, targetId, ...mutationPayload(context) }); },
      async remove(id, context) { await deleteById(client, "tags", id, context); },
    },
    repeats: {
      async list(input) { let query = client.from("repeat_rules").select("*").order("next_occurrence_at"); if (!input?.includeStopped) query = query.is("stopped_at", null); return camelize(unwrap(await query)) as RepeatRule[]; },
      async create(input, context) { return insertOne<RepeatRule>(client, "repeat_rules", withMutation({ ...input, nextOccurrenceAt: input.mode === "scheduled" ? `${advanceRepeatDate(input.startsOn, input.unit, input.interval)}T00:00:00.000Z` : null }, context)); },
      async update(id, input, scope, context) { return rpc<RepeatRule>("update_repeat_rule", { repeatRuleId: id, input, scope, ...mutationPayload(context) }); },
      async pause(id, context) { return rpc<RepeatRule>("transition_repeat_rule", { repeatRuleId: id, transition: "pause", ...mutationPayload(context) }); },
      async resume(id, context) { return rpc<RepeatRule>("transition_repeat_rule", { repeatRuleId: id, transition: "resume", ...mutationPayload(context) }); },
      async stop(id, context) { return rpc<RepeatRule>("transition_repeat_rule", { repeatRuleId: id, transition: "stop", ...mutationPayload(context) }); },
      async processDue(now, timezone, context) { return rpc("process_due_repeats", { processAt: now, userTimezone: timezone, ...mutationPayload(context) }); },
    },
    reminders: {
      async list(taskId) { let query = client.from("reminders").select("*").order("remind_at"); if (taskId) query = query.eq("task_id", taskId); return camelize(unwrap(await query)) as Reminder[]; },
      async upsert() { throw new BackendError("unsupported", "Reminders are retired and cannot be created or changed.", { retryable: false }); },
      async markDelivered() { throw new BackendError("unsupported", "Reminder delivery is retired.", { retryable: false }); },
      async markFailed() { throw new BackendError("unsupported", "Reminder delivery is retired.", { retryable: false }); },
      async retryDelivery() { throw new BackendError("unsupported", "Reminder delivery is retired.", { retryable: false }); },
      async registerPushSubscription() { throw new BackendError("unsupported", "Notifications are retired.", { retryable: false }); },
      async remove() { throw new BackendError("unsupported", "Reminder data is retained for export and cannot be changed.", { retryable: false }); },
    },
    views: {
      async inbox(request) { return viewTasks(client, "inbox_tasks", {}, request); },
      async today(date, request) { return viewTasks(client, "today_tasks", { viewDate: date }, request); },
      async upcoming(from, to, request) { return viewTasks(client, "upcoming_tasks", { dateFrom: from, dateTo: to }, request); },
      async anytime(request) { return viewTasks(client, "anytime_tasks", {}, request); },
      async someday(request) { return { tasks: await viewTasks(client, "someday_tasks", {}, request), projects: await listProjects(client, { attentions: ["someday"] }, "manual", request) }; },
      async deadlines(request) { return viewTasks(client, "deadline_tasks", {}, request); },
      async logbook(from, to, request) { return viewTasks(client, "logbook_tasks", { dateFrom: from, dateTo: to }, request); },
    },
    search: {
      async search(query, request) {
        const taskNumber = parseTaskNumber(query.text);
        if (taskNumber !== null && (!query.types || query.types.includes("task"))) {
          const result = await client.from("tasks").select(TASK_SELECT).eq("task_number", taskNumber).is("deleted_at", null).maybeSingle();
          if (result.error) throw mapError(result.error);
          if (!result.data) return { items: [], nextCursor: null, total: 0 };
          const task = normalizeTaskSummary(camelize(result.data) as Record<string, unknown>);
          const hit: SearchHit = { id: task.id, targetId: task.id, type: "task", title: task.title, subtitle: `${taskNumberLabel(task.taskNumber)}${task.project?.name || task.area?.name ? ` · ${task.project?.name ?? task.area?.name}` : ""}`, highlights: [query.text], matchField: "task_number", score: 100 };
          return { items: [hit], nextCursor: null, total: 1 };
        }
        return rpc<PageResult<SearchHit>>("search_all", { query, page: request ?? {} }, request?.signal);
      },
    },
    realtime: {
      subscribe(entities, listener) {
        const channel = client.channel(`todo:${crypto.randomUUID()}`);
        for (const entity of entities) {
          const table = realtimeTable(entity);
          channel.on("postgres_changes", { event: "*", schema: "public", table }, (payload) => {
            const record = (payload.new && Object.keys(payload.new).length ? payload.new : payload.old) as Record<string, unknown>;
            listener({ entity, operation: payload.eventType === "INSERT" ? "insert" : payload.eventType === "DELETE" ? "delete" : "update", id: String(record.id ?? ""), ownerId: String(record.owner_id ?? "") });
          });
        }
        channel.subscribe();
        return () => { void client.removeChannel(channel); };
      },
    },
    data: {
      async exportAll(signal) {
        const exported = await rpc<DataExport>("export_all", {}, signal);
        exported.attachmentContents = {};
        for (const attachment of exported.attachments ?? []) exported.attachmentContents[attachment.id] = await attachmentBase64(await backend.attachments.download(attachment.id, signal));
        return exported;
      },
      async importAll(data, options, context) {
        const inspected = inspectAttachmentImport(data);
        const preview = await rpc<ImportResult>("import_all", { data: { ...data, attachmentContents: {} }, options: { ...options, dryRun: true }, ...mutationPayload(context) });
        if (options.dryRun) return { ...preview, failed: (preview.failed ?? 0) + inspected.errors.length, errors: [...(preview.errors ?? []), ...inspected.errors] };
        if (inspected.errors.length || preview.failed) throw new BackendError("validation", inspected.errors[0] ?? preview.errors[0] ?? "Import validation failed.");
        const staging: Record<string, string> = {};
        if (inspected.files.length) {
          const { data: sessionData } = await client.auth.getSession();
          const session = sessionData.session;
          if (!session) throw new BackendError("unauthenticated", "Sign in first.");
          for (const { attachment, blob } of inspected.files) {
            const { data: row, error } = await client.functions.invoke("attachment-service", { body: { action: "reserve-import", file: attachment, clientMutationId: context.clientMutationId } });
            if (error) throw new BackendError("unknown", "An import file could not be reserved. No data was imported.", { retryable: true });
            if (row.state !== "verified") {
              await uploadResumable({ url: config.url, token: session.access_token, ownerId: session.user.id, attachmentId: row.id, storagePath: row.storage_path, mediaType: attachment.mediaType, file: blob });
              const verification = await client.functions.invoke("attachment-service", { body: { action: "finalize-import", attachmentId: row.id } });
              if (verification.error) throw new BackendError("validation", "An import file could not be verified. No data was imported.", { retryable: true });
            }
            staging[attachment.id] = row.id;
          }
        }
        const result = await rpc<Partial<ImportResult>>("import_all", { data: { ...data, attachmentContents: {}, attachmentStaging: staging }, options, ...mutationPayload(context) });
        return { created: result.created ?? 0, updated: result.updated ?? 0, skipped: result.skipped ?? 0, conflicts: result.conflicts ?? result.skipped ?? 0, failed: result.failed ?? result.errors?.length ?? 0, errors: result.errors ?? [] };
      },
      async consumeScheduleMigration(signal) { return rpc<{ affectedTasks: number }>("consume_schedule_migration", {}, signal); },
      async deleteAll(context) { await rpcVoid("delete_all_user_data", mutationPayload(context)); },
      async deleteAccount(context) { await rpcVoid("delete_current_account", mutationPayload(context)); },
    },
    personalAccessTokens: {
      async list() { const value = await patRequest<{ items: unknown[] }>(client, config, "tokens"); return camelize(value.items) as PersonalAccessToken[]; },
      async create(input) { return camelize(await patRequest(client, config, "tokens", { method: "POST", body: JSON.stringify(input) })) as CreatedPersonalAccessToken; },
      async revoke(id) { await patRequest(client, config, `tokens/${encodeURIComponent(id)}`, { method: "DELETE" }); },
    },
  };

  return backend;
}

async function patRequest<T>(client: SupabaseClient, config: SupabaseConfig, path: string, init: RequestInit = {}): Promise<T> {
  const { data, error } = await client.auth.getSession();
  if (error || !data.session) throw new BackendError("unauthenticated", "Sign in first.");
  const response = await fetch(`${config.url}/functions/v1/pat-api/${path}`, { ...init, headers: { apikey: config.publishableKey, authorization: `Bearer ${data.session.access_token}`, "content-type": "application/json", ...init.headers } });
  if (response.status === 204) return undefined as T;
  const payload = await response.json().catch(() => ({})) as { error?: string };
  if (!response.ok) throw new BackendError(response.status === 401 ? "unauthenticated" : response.status === 403 ? "forbidden" : response.status === 409 ? "conflict" : "unknown", payload.error ?? "PAT request failed.");
  return payload as T;
}

async function currentUserId(client: SupabaseClient): Promise<string> {
  const { data, error } = await client.auth.getUser();
  if (error) throw mapError(error);
  if (!data.user) throw new BackendError("unauthenticated", "Sign in first.");
  return data.user.id;
}

async function selectSingle<T>(client: SupabaseClient, table: string, select: string): Promise<T> {
  const result = await client.from(table).select(select).single();
  return camelize(unwrap(result)) as T;
}

async function selectById<T>(client: SupabaseClient, table: string, id: EntityId): Promise<T> {
  const result = await client.from(table).select("*").eq("id", id).single();
  return camelize(unwrap(result)) as T;
}

async function insertOne<T>(client: SupabaseClient, table: string, input: unknown): Promise<T> {
  const result = await client.from(table).insert(snakeize(input) as never).select("*").single();
  return camelize(unwrap(result)) as T;
}

async function updateSingle<T>(client: SupabaseClient, table: string, id: EntityId, input: unknown, context: MutationContext): Promise<T> {
  let query = client.from(table).update(snakeize({ ...input as object, clientMutationId: context.clientMutationId }) as never).eq("id", id);
  if (context.expectedVersion !== undefined) query = query.eq("version", context.expectedVersion);
  const result = await query.select("*").single();
  return camelize(unwrap(result)) as T;
}

async function deleteById(client: SupabaseClient, table: string, id: EntityId, context: MutationContext): Promise<void> {
  let query = client.from(table).delete().eq("id", id);
  if (context.expectedVersion !== undefined) query = query.eq("version", context.expectedVersion);
  unwrapVoid(await query);
}

async function getTask(client: SupabaseClient, id: EntityId): Promise<TaskSummary> {
  const result = await client.from("tasks").select(TASK_SELECT).eq("id", id).single();
  return normalizeTaskSummary(camelize(unwrap(result)) as Record<string, unknown>);
}

async function listTasks(client: SupabaseClient, filter?: TaskFilter, sort: TaskSort = "manual", request?: PageRequest): Promise<PageResult<TaskSummary>> {
  let query = client.from("tasks").select(TASK_SELECT, { count: "exact" });
  if (!filter?.includeDeleted) query = query.is("deleted_at", null);
  if (filter?.statuses?.length) query = query.in("status", filter.statuses);
  if (filter?.scheduleKinds?.length) query = query.in("schedule_kind", filter.scheduleKinds);
  if (filter?.areaIds?.length) query = query.in("area_id", filter.areaIds);
  if (filter?.projectIds?.length) query = query.in("project_id", filter.projectIds);
  if (filter?.headingIds?.length) query = query.in("heading_id", filter.headingIds);
  if (filter?.startsFrom) query = query.gte("start_date", filter.startsFrom);
  if (filter?.startsTo) query = query.lte("start_date", filter.startsTo);
  if (filter?.deadlineFrom) query = query.gte("deadline", filter.deadlineFrom);
  if (filter?.deadlineTo) query = query.lte("deadline", filter.deadlineTo);
  const sortColumn = ({ manual: "position", number: "task_number", created: "created_at", updated: "updated_at", start_date: "start_date", deadline: "deadline", completed: "completed_at" } as const)[sort];
  query = query.order(sortColumn, { ascending: sort === "manual" || sort === "number" || sort === "start_date" || sort === "deadline" });
  const start = request?.cursor ? Number(request.cursor) : 0;
  const limit = request?.limit ?? 100;
  const result = await query.range(start, start + limit - 1);
  let items = (camelize(unwrap(result)) as Record<string, unknown>[]).map(normalizeTaskSummary);
  if (filter?.tagIds?.length) items = items.filter((task) => filter.tagMatch === "any" ? filter.tagIds!.some((tagId) => task.tags.some((tag) => tag.id === tagId)) : filter.tagIds!.every((tagId) => task.tags.some((tag) => tag.id === tagId)));
  return { items, nextCursor: start + limit < (result.count ?? 0) ? String(start + limit) : null, total: result.count ?? items.length };
}

interface ProjectCountRow { projectId: string; open: number; completed: number; cancelled: number; total: number; }

async function projectTimezone(client: SupabaseClient): Promise<string> {
  const result = await client.from("preferences").select("timezone").maybeSingle();
  return (camelize(unwrap(result)) as { timezone?: string }).timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
}

async function getProject(client: SupabaseClient, id: EntityId): Promise<ProjectSummary> {
  const result = await client.from("projects").select(PROJECT_SELECT).eq("id", id).single();
  const raw = camelize(unwrap(result)) as Record<string, unknown>;
  const progress = await client.rpc("project_progress", { project_id: id }) as QueryResult<unknown>;
  const timezone = await projectTimezone(client);
  return normalizeProjectSummary(raw, camelize(unwrap(progress)) as ProjectSummary["progress"], timezone);
}

async function listProjects(client: SupabaseClient, filter?: ProjectFilter, sort: ProjectSort = "manual", request?: PageRequest): Promise<PageResult<ProjectSummary>> {
  let query = client.from("projects").select(PROJECT_SELECT, { count: "exact" });
  if (filter?.areaIds?.length) query = query.in("area_id", filter.areaIds);
  const column = ({ manual: "position", updated: "updated_at", deadline: "deadline", name: "name" } as const)[sort];
  query = query.order(column, { ascending: sort === "manual" || sort === "deadline" || sort === "name" });
  const start = request?.cursor ? Number(request.cursor) : 0;
  const limit = request?.limit ?? 100;
  const result = await query.range(start, start + limit - 1);
  const countsResult = await client.rpc("project_task_counts") as QueryResult<unknown>;
  const timezone = await projectTimezone(client);
  const counts = new Map((camelize(unwrap(countsResult)) as ProjectCountRow[]).map((row) => [row.projectId, row]));
  let items = (camelize(unwrap(result)) as Record<string, unknown>[]).map((raw) => {
    const row = counts.get(String(raw.id));
    const open = row?.open ?? 0;
    const completed = row?.completed ?? 0;
    const total = open + completed;
    return normalizeProjectSummary(raw, { open, completed, cancelled: row?.cancelled ?? 0, total, ratio: total ? completed / total : 0 }, timezone);
  });
  if (filter?.attentions?.length) items = items.filter((project) => filter.attentions!.includes(project.attention));
  if (filter?.weeklyFocus !== undefined) items = items.filter((project) => Boolean(project.weeklyFocus) === filter.weeklyFocus);
  if (filter?.stalled !== undefined) items = items.filter((project) => project.isStalled === filter.stalled);
  if (filter?.tagIds?.length) items = items.filter((project) => filter.tagMatch === "any" ? filter.tagIds!.some((tagId) => project.tags.some((tag) => tag.id === tagId)) : filter.tagIds!.every((tagId) => project.tags.some((tag) => tag.id === tagId)));
  return { items, nextCursor: start + limit < (result.count ?? 0) ? String(start + limit) : null, total: result.count ?? items.length };
}

async function viewTasks(client: SupabaseClient, functionName: string, args: Record<string, unknown>, request?: PageRequest): Promise<PageResult<TaskSummary>> {
  const result = await client.rpc(functionName, snakeize({ ...args, cursor: request?.cursor ?? null, pageSize: request?.limit ?? 100 }) as never) as QueryResult<unknown>;
  const value = camelize(unwrap(result)) as { items: Record<string, unknown>[]; nextCursor: string | null; total: number };
  return { ...value, items: value.items.map(normalizeTaskSummary) };
}

export function normalizeTaskSummary(raw: Record<string, unknown>): TaskSummary {
  const links = Array.isArray(raw.taskTags) ? raw.taskTags as Array<{ tag?: Tag }> : [];
  return normalizeLegacyTaskSummary({
    ...(raw as unknown as Task),
    area: (raw.area as Area | null | undefined) ?? null,
    project: (raw.project as Project | null | undefined) ?? null,
    heading: (raw.heading as Heading | null | undefined) ?? null,
    tags: links.map((link) => link.tag).filter((tag): tag is Tag => Boolean(tag)),
    checklist: Array.isArray(raw.checklist) ? raw.checklist as ChecklistItem[] : [],
  });
}

function normalizeProjectSummary(raw: Record<string, unknown>, progress: ProjectSummary["progress"], timezone: string): ProjectSummary {
  const links = Array.isArray(raw.projectTags) ? raw.projectTags as Array<{ tag?: Tag }> : [];
  const focuses = Array.isArray(raw.weeklyFocus) ? raw.weeklyFocus as WeeklyFocus[] : [];
  const project = raw as unknown as Project;
  const attention = projectAttention(project, progress, timezone);
  const stalled = attention === "active" && Date.now() - Date.parse(project.lastActivityAt) >= 2 * 86_400_000 && (!project.stallIgnoredUntil || Date.parse(project.stallIgnoredUntil) <= Date.now());
  return normalizeLegacyProjectSummary({ ...project, area: (raw.area as Area | null | undefined) ?? null, progress, attention, tags: links.map((link) => link.tag).filter((tag): tag is Tag => Boolean(tag)), weeklyFocus: focuses[0] ?? null, isStalled: stalled });
}

function mapSession(session: Session): AuthSession {
  return { user: { id: session.user.id, email: session.user.email ?? "", displayName: String(session.user.user_metadata.display_name ?? session.user.email ?? "User") }, expiresAt: session.expires_at ? new Date(session.expires_at * 1000).toISOString() : null };
}

function mapAuthEvent(event: AuthChangeEvent, session: Session | null): AuthEvent {
  if (event === "SIGNED_OUT") return { type: "signed_out" };
  if (event === "PASSWORD_RECOVERY") return { type: "password_recovery", session: session ? mapSession(session) : null };
  if (event === "USER_UPDATED" && session) return { type: "user_updated", session: mapSession(session) };
  if (event === "INITIAL_SESSION") return { type: "initial", session: session ? mapSession(session) : null };
  if (session) return { type: "signed_in", session: mapSession(session) };
  return { type: "initial", session: null };
}

function unwrap<T>(result: QueryResult<T>): T {
  if (result.error) throw mapError(result.error);
  if (result.data === null) throw new BackendError("not_found", "The requested content does not exist.", { details: result });
  return result.data;
}

export function unwrapVoid(result: QueryResult<unknown>): void {
  if (result.error) throw mapError(result.error);
}

function mapError(error: QueryError): BackendError {
  const code = error.code;
  if (code === "invalid_credentials") return new BackendError("unauthenticated", "The email or password is incorrect.", { cause: error });
  if (code === "email_not_confirmed") return new BackendError("unauthenticated", "Verify your email before signing in.", { cause: error });
  if (code === "user_already_exists" || /already registered/i.test(error.message)) return new BackendError("conflict", "This email is already registered. Sign in instead.", { cause: error });
  if (code === "over_email_send_rate_limit") return new BackendError("rate_limited", "Too many emails were requested. Try again later.", { cause: error });
  if (code === "weak_password") return new BackendError("validation", "Use a longer or more complex password.", { cause: error });
  if (code === "PGRST116") return new BackendError("not_found", "The requested content does not exist.", { cause: error });
  if (code === "23505") return new BackendError("conflict", "Matching content already exists.", { cause: error });
  if (code === "42501") return new BackendError("forbidden", "You do not have permission to perform this operation.", { cause: error });
  if (code === "22023") return new BackendError("validation", error.message || "The input is invalid.", { cause: error });
  if (code === "PGRST202") return new BackendError("unsupported", "The database is missing a required feature. Apply the latest migration and retry.", { cause: error });
  if (code === "40001" || code === "P0002") return new BackendError("conflict", "The content changed elsewhere. Refresh and retry.", { cause: error });
  return new BackendError("unknown", error.message || "The service is temporarily unavailable.", { retryable: true, details: error, cause: error });
}

function mutationPayload(context: MutationContext): Record<string, unknown> {
  return { clientMutationId: context.clientMutationId, expectedVersion: context.expectedVersion ?? null };
}

function withMutation(input: unknown, context: MutationContext): Record<string, unknown> {
  return { ...(input as object), clientMutationId: context.clientMutationId };
}

function camelize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(camelize);
  if (!value || typeof value !== "object" || value instanceof Date) return value;
  return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([key, item]) => [key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase()), camelize(item)]));
}

function snakeize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(snakeize);
  if (!value || typeof value !== "object" || value instanceof Date) return value;
  return Object.fromEntries(Object.entries(value as Record<string, unknown>).filter(([, item]) => item !== undefined).map(([key, item]) => [key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`), snakeize(item)]));
}

function realtimeTable(entity: RealtimeEntity): string {
  return ({ profile: "profiles", preferences: "preferences", area: "areas", project: "projects", heading: "headings", task: "tasks", comment: "task_comments", attachment: "attachments", checklist: "checklist_items", tag: "tags", repeat: "repeat_rules", reminder: "reminders", weekly_focus: "weekly_focus" })[entity];
}

function advanceRepeatDate(date: string, unit: "day" | "week" | "month" | "year", interval: number) {
  const value = new Date(`${date}T12:00:00Z`);
  if (unit === "day") value.setUTCDate(value.getUTCDate() + interval);
  else if (unit === "week") value.setUTCDate(value.getUTCDate() + interval * 7);
  else if (unit === "month") value.setUTCMonth(value.getUTCMonth() + interval);
  else value.setUTCFullYear(value.getUTCFullYear() + interval);
  return value.toISOString().slice(0, 10);
}
